import { useGlobalData } from "@/app/global_data"
import { useAppData } from "@/app/providers/app_data.provider"
import { Entity } from "@/app/types"
import {
  flowMonitorKeys,
  flowMonitorParameters,
} from "@/components/tableau/utils"
import { useCallback, useEffect, useMemo, useState } from "react"

type UseTableauVizInteractiveParams = {
  slotKey: string | null
  changeParameterValue: (paramName: string, newValue: any) => Promise<void>
}

export function useTableauVizInteractive({
  slotKey,
  changeParameterValue,
}: UseTableauVizInteractiveParams) {
  if (!slotKey) return null

  const globalData = useGlobalData()
  const { entity, term, comparison, entityValues, isLoaded } = globalData.get()
  const { firmsList, lookup } = useAppData()

  const flags = useMemo(
    () => ({
      isPercentileTracker: slotKey === flowMonitorKeys.PERCENTILE_TRACKER,
      isCompetitivePositioning:
        slotKey === flowMonitorKeys.COMPETITIVE_POSITIONING,
      isGrowthGauge: slotKey === flowMonitorKeys.GROWTH_GAUGE,
      isAssetMomentum: slotKey === flowMonitorKeys.ASSET_MOMENTUM,
      isLeadersLaggards: slotKey === flowMonitorKeys.LEADERS_LAGGARDS,
    }),
    [slotKey]
  )

  const [isEntityEnabled, setIsEntityEnabled] = useState(
    flags.isAssetMomentum ? true : true
  )
  const [isComparisonEnabled, setIsComparisonEnabled] = useState(
    flags.isAssetMomentum ? false : true
  )

  const entityRefId = useMemo(() => {
    if (!entity) return null

    if (entity.isFund()) {
      const entityFirm = lookup.getFirm(entity.getFund().firmId)
      return entityFirm?.refId || null
    } else {
      const firm = lookup.getFirm(entity.getFirm().firmId)
      return firm?.refId || null
    }
  }, [entity, lookup])

  const comparisonRefIds = useMemo(() => {
    if (!comparison) return ""

    if (comparison.isAll()) {
      return ""
    } else if (comparison.isCustomList()) {
      return (
        entityValues?.firmsets?.customMetrics?.members
          ?.map((m) => m.firm?.refId)
          .filter(Boolean)
          .join("|") || ""
      )
    } else {
      return (
        entityValues?.firmsets?.peers?.members
          ?.map((m) => m.firm?.refId)
          .filter(Boolean)
          .join("|") || ""
      )
    }
  }, [comparison, entityValues])

  const selectEntity = useCallback(
    async (checked: boolean) => {
      if (flags.isAssetMomentum) {
        if (!checked && !isComparisonEnabled) return
        if (checked && isComparisonEnabled) {
          setIsComparisonEnabled(false)
          try {
            await changeParameterValue(
              "p.member_firm_id",
              flowMonitorParameters.DISABLED_COMPARISON_REF_ID
            )
          } catch (error) {
            console.error("Failed to disable comparison parameter:", error)
          }
        }
      } else {
        if (!checked && !isComparisonEnabled) return
      }

      setIsEntityEnabled(checked)

      try {
        if (!checked) {
          await changeParameterValue(
            "p.ref_firm_id",
            flowMonitorParameters.DISABLED_ENTITY_REF_ID
          )
        } else if (entityRefId) {
          await changeParameterValue("p.ref_firm_id", entityRefId)
        }
      } catch (error) {
        console.error("Failed to update entity parameter:", error)
        setIsEntityEnabled(!checked)
      }
    },
    [
      isComparisonEnabled,
      entityRefId,
      changeParameterValue,
      flags.isAssetMomentum,
    ]
  )

  const selectComparison = useCallback(
    async (checked: boolean) => {
      if (flags.isAssetMomentum) {
        if (!checked && !isEntityEnabled) return
        if (checked && isEntityEnabled) {
          setIsEntityEnabled(false)
          try {
            await changeParameterValue(
              "p.ref_firm_id",
              flowMonitorParameters.DISABLED_ENTITY_REF_ID
            )
          } catch (error) {
            console.error("Failed to disable entity parameter:", error)
          }
        }
      } else {
        if (!checked && !isEntityEnabled) return
      }

      setIsComparisonEnabled(checked)

      try {
        if (!checked) {
          await changeParameterValue(
            "p.member_firm_id",
            flowMonitorParameters.DISABLED_COMPARISON_REF_ID
          )

          if (flags.isGrowthGauge)
            await changeParameterValue(
              "val_growth_type",
              flowMonitorParameters.VAL_GROWTH_TYPE_ID
            )
        } else {
          await changeParameterValue("p.member_firm_id", comparisonRefIds)
        }
      } catch (error) {
        console.error("Failed to update comparison parameter:", error)
        setIsComparisonEnabled(!checked)
      }
    },
    [
      isEntityEnabled,
      comparisonRefIds,
      changeParameterValue,
      flags.isAssetMomentum,
      flags.isGrowthGauge,
    ]
  )

  const handleFirmSelection = useCallback(
    (id: string) => {
      const firm = firmsList.find((f) => f.firmId === id)
      if (!firm) return

      const newEntity = new Entity(firm)
      globalData.setEntity(newEntity).notify()

      if (isEntityEnabled) {
        changeParameterValue("p.ref_firm_id", firm.refId).catch((error) => {
          console.error("Failed to update firm parameter:", error)
        })
      }
    },
    [firmsList, globalData, isEntityEnabled, changeParameterValue]
  )

  useEffect(() => {
    async function updateParameters() {
      try {
        if (isEntityEnabled && entityRefId) {
          await changeParameterValue("p.ref_firm_id", entityRefId)
        }

        if (isComparisonEnabled) {
          if (!comparison.isAll()) {
            await changeParameterValue("UniverseScreen", "Peer Group")
            if (comparisonRefIds) {
              await changeParameterValue("p.member_firm_id", comparisonRefIds)
            }
          } else {
            await changeParameterValue("UniverseScreen", "Industry")
          }
        }
      } catch (error) {
        console.error("Failed to update parameters:", error)
      }
    }

    updateParameters()
  }, [entityRefId, comparisonRefIds, isEntityEnabled, isComparisonEnabled])

  return {
    entity,
    term,
    comparison,
    isLoaded,
    flags,
    isEntityEnabled,
    isComparisonEnabled,
    entityRefId,
    comparisonRefIds,
    selectEntity,
    selectComparison,
    handleFirmSelection,
  }
}
